import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Search,
} from "lucide-react";
import i18n from "i18next";
import { SectionCard } from "./SectionCard";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils/cn";
import { formatNumber } from "../utils";
import {
  useLlmConfigs,
  type LlmConfigOrderBy,
  type LlmConfigRow,
  type LlmConfigSource,
  type StatsFilters,
} from "../api";

type Props = { filters: StatsFilters };

const PROVIDER_OPTION_VALUES = [
  "all",
  "openai",
  "groq",
  "anthropic",
  "private",
] as const;

const SOURCE_OPTION_VALUES = ["all", "user", "public"] as const;

const TYPE_OPTION_VALUES = [
  "all",
  "chat",
  "multimodal",
  "embedding",
  "speech_to_text",
  "parsing",
] as const;

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const TYPE_LABEL_TOKENS: Record<string, string> = {
  chat: "usageAndCost.llmConfigs.types.chat",
  multimodal: "usageAndCost.llmConfigs.types.multimodal",
  embedding: "usageAndCost.llmConfigs.types.embedding",
  speech_to_text: "usageAndCost.llmConfigs.types.speechToText",
  text_to_speech: "usageAndCost.llmConfigs.types.textToSpeech",
  parsing: "usageAndCost.llmConfigs.types.parsing",
  image_generation: "usageAndCost.llmConfigs.types.imageGeneration",
};

function useTypeLabel() {
  const { t } = useTranslation();
  return (type: string) => {
    const token = TYPE_LABEL_TOKENS[type];
    return token ? t(token) : type;
  };
}

const PROVIDER_COLORS: Record<string, string> = {
  openai: "#10a37f",
  groq: "#f55036",
  anthropic: "#d97706",
  private: "#6366f1",
};

function ProviderAvatar({ provider }: { provider: string | null }) {
  const key = (provider ?? "?").toLowerCase();
  const color = PROVIDER_COLORS[key] ?? "#71717a";
  const initial = (provider ?? "?").charAt(0).toUpperCase();
  return (
    <span
      className="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
      style={{ backgroundColor: color }}
    >
      {initial}
    </span>
  );
}

type SortKey = LlmConfigOrderBy;

function SortHeader({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: "asc" | "desc";
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
    >
      <span>{label}</span>
      <span className="flex flex-col leading-[0.5] text-muted-foreground/60">
        <ChevronUp
          className={cn(
            "size-3",
            active && dir === "asc" && "text-foreground",
          )}
        />
        <ChevronDown
          className={cn(
            "-mt-1 size-3",
            active && dir === "desc" && "text-foreground",
          )}
        />
      </span>
    </button>
  );
}

export function LlmConfigsTable({ filters }: Props) {
  const { t } = useTranslation();
  const typeLabel = useTypeLabel();
  const isRTL = i18n.dir() === "rtl";
  const [search, setSearch] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [provider, setProvider] = React.useState("all");
  const [source, setSource] = React.useState("all");
  const [type, setType] = React.useState("all");
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [orderBy, setOrderBy] = React.useState<SortKey>("spend");
  const [orderDir, setOrderDir] = React.useState<"asc" | "desc">("desc");

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  React.useEffect(() => {
    setPage(1);
  }, [debouncedSearch, provider, source, type, pageSize]);

  const { data, isLoading, isFetching } = useLlmConfigs(filters, {
    providers: provider === "all" ? undefined : [provider],
    sources: source === "all" ? undefined : [source as LlmConfigSource],
    types: type === "all" ? undefined : [type],
    search: debouncedSearch || undefined,
    orderBy,
    orderDir,
    page,
    pageSize,
  });

  const rows: LlmConfigRow[] = data?.data ?? [];
  const totalItems = data?.metadata.total_items ?? 0;
  const totalPages = Math.max(1, data?.metadata.total_pages ?? 1);

  const toggleSort = (key: SortKey) => {
    if (orderBy === key) {
      setOrderDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(key);
      setOrderDir("desc");
    }
  };

  const pageNumbers = getPageNumbers(page, totalPages);

  return (
    <SectionCard bodyClassName="p-0">
      <div className="flex flex-wrap items-center justify-end gap-2 px-5 pt-5">
        <div className="relative me-auto">
          <Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("usageAndCost.llmConfigs.searchPlaceholder")}
            className="h-10 w-[280px] rounded-lg border border-input bg-transparent ps-9 pe-3 text-sm text-foreground placeholder:text-muted-foreground outline-hidden transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
          />
        </div>
        <Select value={provider} onValueChange={setProvider}>
          <SelectTrigger className="h-10 w-[160px] rounded-lg">
            <SelectValue
              placeholder={t("usageAndCost.llmConfigs.allProviders")}
            />
          </SelectTrigger>
          <SelectContent align="end">
            {PROVIDER_OPTION_VALUES.map((value) => (
              <SelectItem key={value} value={value}>
                {value === "all"
                  ? t("usageAndCost.llmConfigs.allProviders")
                  : t(`usageAndCost.llmConfigs.providers.${value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="h-10 w-[140px] rounded-lg">
            <SelectValue
              placeholder={t("usageAndCost.llmConfigs.allSources")}
            />
          </SelectTrigger>
          <SelectContent align="end">
            {SOURCE_OPTION_VALUES.map((value) => (
              <SelectItem key={value} value={value}>
                {value === "all"
                  ? t("usageAndCost.llmConfigs.allSources")
                  : t(`usageAndCost.llmConfigs.sources.${value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="h-10 w-[140px] rounded-lg">
            <SelectValue placeholder={t("usageAndCost.llmConfigs.allTypes")} />
          </SelectTrigger>
          <SelectContent align="end">
            {TYPE_OPTION_VALUES.map((value) => (
              <SelectItem key={value} value={value}>
                {value === "all"
                  ? t("usageAndCost.llmConfigs.allTypes")
                  : typeLabel(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full table-fixed text-sm">
          <colgroup>
            <col />
            <col className="w-[180px]" />
            <col className="w-[140px]" />
          </colgroup>
          <thead>
            <tr className="border-b border-border">
              <th className="px-5 py-3 text-start">
                <SortHeader
                  label={t("usageAndCost.llmConfigs.modelInformation")}
                  active={orderBy === "model"}
                  dir={orderDir}
                  onClick={() => toggleSort("model")}
                />
              </th>
              <th className="px-5 py-3 text-start">
                <SortHeader
                  label={t("usageAndCost.llmConfigs.type")}
                  active={orderBy === "type"}
                  dir={orderDir}
                  onClick={() => toggleSort("type")}
                />
              </th>
              <th className="px-5 py-3 text-end">
                <SortHeader
                  label={t("usageAndCost.llmConfigs.requests")}
                  active={orderBy === "requests"}
                  dir={orderDir}
                  onClick={() => toggleSort("requests")}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: Math.min(pageSize, 10) }).map((_, i) => (
                <tr
                  key={i}
                  className="border-b border-border last:border-b-0"
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="size-7 rounded-full" />
                      <div className="flex flex-col gap-1">
                        <Skeleton className="h-3.5 w-[200px]" />
                        <Skeleton className="h-3 w-[260px]" />
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <Skeleton className="h-5 w-20" />
                  </td>
                  <td className="px-5 py-3 text-end">
                    <Skeleton className="ms-auto h-4 w-12" />
                  </td>
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={3}
                  className="px-5 py-10 text-center text-sm text-muted-foreground"
                >
                  {t("usageAndCost.llmConfigs.noModelsFound")}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-border last:border-b-0"
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <ProviderAvatar provider={row.provider} />
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate text-sm text-foreground">
                          {row.model}
                        </span>
                        <span className="truncate font-mono text-xs text-muted-foreground">
                          {row.id}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <Badge variant="outline" className="font-normal">
                      {typeLabel(row.type)}
                    </Badge>
                  </td>
                  <td className="px-5 py-3 text-end text-foreground">
                    {row.requests > 0 ? formatNumber(row.requests) : "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{t("usageAndCost.llmConfigs.rowsPerPage")}</span>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => setPageSize(Number(v))}
          >
            <SelectTrigger size="sm" className="h-8 w-[72px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || isFetching}
            className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs text-muted-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isRTL ? (
              <ChevronRight className="size-3.5" />
            ) : (
              <ChevronLeft className="size-3.5" />
            )}
            {t("usageAndCost.llmConfigs.previous")}
          </button>
          {pageNumbers.map((n, i) =>
            n === "ellipsis" ? (
              <span
                key={`e-${i}`}
                className="inline-flex size-8 items-center justify-center text-xs text-muted-foreground"
              >
                …
              </span>
            ) : (
              <button
                key={n}
                type="button"
                onClick={() => setPage(n)}
                className={cn(
                  "inline-flex size-8 items-center justify-center rounded-md text-xs",
                  n === page
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                {n}
              </button>
            ),
          )}
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || isFetching}
            className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs text-muted-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("usageAndCost.llmConfigs.next")}
            {isRTL ? (
              <ChevronLeft className="size-3.5" />
            ) : (
              <ChevronRight className="size-3.5" />
            )}
          </button>
        </div>

        <div className="text-xs text-muted-foreground">
          {totalItems > 0
            ? t("usageAndCost.llmConfigs.paginationRange", {
                from: (page - 1) * pageSize + 1,
                to: Math.min(page * pageSize, totalItems),
                total: totalItems,
              })
            : t("usageAndCost.llmConfigs.noResults")}
        </div>
      </div>
    </SectionCard>
  );
}

function getPageNumbers(
  current: number,
  total: number,
): (number | "ellipsis")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages: (number | "ellipsis")[] = [1];
  if (current > 3) pages.push("ellipsis");
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (current < total - 2) pages.push("ellipsis");
  pages.push(total);
  return pages;
}
